import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { MobileNavProvider, useMobileNav } from './context/MobileNav';
import useIsMobile from './utils/useIsMobile';
import { COMPANY_NAME } from './config/branding';
import Navbar, { TopUserMenu, ChangePwdModal } from './components/shared/Navbar';
import Login from './pages/Login';
import Register from './pages/Register';
import TicketList from './pages/customer/TicketList';
import NewTicket from './pages/customer/NewTicket';
import TicketDetail from './pages/TicketDetail';
import Dashboard from './pages/admin/Dashboard';
import TicketCalendar from './pages/admin/TicketCalendar';
import MonthlyReport from './pages/admin/MonthlyReport';
import KnowledgeManagement from './pages/admin/KnowledgeManagement';
import Maintenance from './pages/admin/Maintenance';
import AdminTickets from './pages/admin/AdminTickets';
import Reports from './pages/admin/Reports';
import Customers from './pages/admin/Customers';
import Products from './pages/admin/Products';
import LiveChat from './pages/LiveChat';
import AgentTickets from './pages/agent/AgentTickets';
import SupportUsers from './pages/admin/SupportUsers';
import CustomerLogins from './pages/admin/CustomerLogins';
import CustomerManagerTickets from './pages/customer_manager/CustomerManagerTickets';
import Footer from './components/shared/Footer';
import OOFManager from './pages/manager/OOFManager';
import Projects from './pages/admin/Projects';
import ProjectDetail from './pages/admin/ProjectDetail';
import ProjectTemplates from './pages/admin/ProjectTemplates';
import ProjectsDashboard from './pages/admin/ProjectsDashboard';
import TodaysPlan from './pages/TodaysPlan';
import TeamPlans from './pages/admin/TeamPlans';
import MyTasks from './pages/MyTasks';

function RequireAuth({ children }) {
  const { user } = useAuth();
  return user ? children : <Navigate to="/login" replace />;
}

function RequireAdmin({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin' && user.role !== 'super_admin') return <Navigate to="/" replace />;
  return children;
}

function RequireAgent({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'agent') return <Navigate to="/" replace />;
  return children;
}

function RequireStaff({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!['admin', 'super_admin', 'manager', 'agent'].includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function RequireManager({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'manager') return <Navigate to="/" replace />;
  return children;
}

function RequireCustomer({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'customer') return <Navigate to="/" replace />;
  return children;
}

function RequireCustomerManager({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'customer_manager') return <Navigate to="/" replace />;
  return children;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'admin' || user.role === 'super_admin') return <Navigate to="/admin" replace />;
  if (user.role === 'manager')          return <Navigate to="/manager/tickets" replace />;
  if (user.role === 'agent')            return <Navigate to="/agent/tickets" replace />;
  if (user.role === 'customer_manager') return <Navigate to="/customer-manager/tickets" replace />;
  return <Navigate to="/tickets" replace />;
}

function AppRoutes() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isMobile = useIsMobile();
  const { setDrawerOpen } = useMobileNav();
  const [showChangePwd, setShowChangePwd] = useState(false);
  function handleLogout() { logout(); nav('/login'); }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: user ? 'row' : 'column' }}>
      <Navbar />
      <div className="kg-main" style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflowX: 'hidden' }}>
        {user && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: isMobile
                          ? 'calc(6px + env(safe-area-inset-top)) calc(12px + env(safe-area-inset-right)) 6px calc(12px + env(safe-area-inset-left))'
                          : '8px 24px',
                        borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-surface)', position: 'sticky', top: 0, zIndex: 50 }}>
            {isMobile ? (
              <button onClick={() => setDrawerOpen(true)} aria-label="Open menu"
                style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer', padding: '8px 6px', margin: '-4px 0', WebkitTapHighlightColor: 'transparent' }}>
                <span style={{ fontSize: 24, lineHeight: 1, color: 'var(--text-primary)' }}>☰</span>
                <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-.3px', color: '#0B6623' }}>{COMPANY_NAME}</span>
              </button>
            ) : <span />}
            <TopUserMenu user={user} onLogout={handleLogout} onChangePwd={() => setShowChangePwd(true)} />
          </div>
        )}
      <Routes>
        <Route path="/" element={<HomeRedirect />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />

        {/* Customer routes */}
        <Route path="/tickets" element={<RequireCustomer><TicketList /></RequireCustomer>} />
        <Route path="/tickets/new" element={<RequireCustomer><NewTicket /></RequireCustomer>} />
        <Route path="/tickets/:id" element={<RequireAuth><TicketDetail /></RequireAuth>} />
        <Route path="/chat" element={<RequireCustomer><LiveChat /></RequireCustomer>} />
        <Route path="/knowledge" element={<RequireCustomer><KnowledgeManagement /></RequireCustomer>} />

        {/* Agent routes — only assigned tickets + chat */}
        <Route path="/agent/tickets" element={<RequireAgent><AgentTickets /></RequireAgent>} />
        <Route path="/agent/tickets/:id" element={<RequireAgent><TicketDetail /></RequireAgent>} />
        <Route path="/agent/chat" element={<RequireAgent><LiveChat /></RequireAgent>} />
        <Route path="/agent/today" element={<RequireAgent><TodaysPlan /></RequireAgent>} />
        <Route path="/agent/my-tasks" element={<RequireAgent><MyTasks /></RequireAgent>} />

        {/* Manager routes — all tickets + assign + chat */}
        <Route path="/manager" element={<RequireManager><Dashboard /></RequireManager>} />
        <Route path="/manager/tickets" element={<RequireManager><AdminTickets /></RequireManager>} />
        <Route path="/manager/tickets/:id" element={<RequireManager><TicketDetail /></RequireManager>} />
        <Route path="/manager/chat" element={<RequireManager><LiveChat /></RequireManager>} />
        <Route path="/manager/today" element={<RequireManager><TodaysPlan /></RequireManager>} />
        <Route path="/manager/my-tasks" element={<RequireManager><MyTasks /></RequireManager>} />

        {/* Customer Manager routes */}
        <Route path="/customer-manager/tickets" element={<RequireCustomerManager><CustomerManagerTickets /></RequireCustomerManager>} />
        <Route path="/customer-manager/tickets/:id" element={<RequireCustomerManager><TicketDetail /></RequireCustomerManager>} />

        {/* Admin-only routes */}
        <Route path="/admin" element={<RequireAdmin><Dashboard /></RequireAdmin>} />
        <Route path="/admin/calendar" element={<RequireAdmin><TicketCalendar /></RequireAdmin>} />
        <Route path="/admin/monthly-report" element={<RequireAdmin><MonthlyReport /></RequireAdmin>} />
        <Route path="/admin/today" element={<RequireAdmin><TodaysPlan /></RequireAdmin>} />
        <Route path="/admin/my-tasks" element={<RequireAdmin><MyTasks /></RequireAdmin>} />
        <Route path="/admin/team-plans" element={<RequireAdmin><TeamPlans /></RequireAdmin>} />
        <Route path="/manager/team-plans" element={<RequireManager><TeamPlans /></RequireManager>} />
        <Route path="/manager/calendar" element={<RequireManager><TicketCalendar /></RequireManager>} />
        <Route path="/manager/monthly-report" element={<RequireManager><MonthlyReport /></RequireManager>} />
        <Route path="/admin/tickets" element={<RequireAdmin><AdminTickets /></RequireAdmin>} />
        <Route path="/admin/tickets/:id" element={<RequireAdmin><TicketDetail /></RequireAdmin>} />
        <Route path="/admin/reports" element={<RequireAdmin><Reports /></RequireAdmin>} />
        <Route path="/admin/customers" element={<RequireAdmin><Customers /></RequireAdmin>} />
        <Route path="/admin/products" element={<RequireAdmin><Products /></RequireAdmin>} />
        <Route path="/admin/users" element={<RequireAdmin><SupportUsers /></RequireAdmin>} />
        <Route path="/admin/customer-logins" element={<RequireAdmin><CustomerLogins /></RequireAdmin>} />
        <Route path="/admin/chat" element={<RequireAdmin><LiveChat /></RequireAdmin>} />
        <Route path="/admin/knowledge" element={<RequireAdmin><KnowledgeManagement /></RequireAdmin>} />
        <Route path="/admin/maintenance" element={<RequireAdmin><Maintenance /></RequireAdmin>} />
        <Route path="/manager/knowledge" element={<RequireManager><KnowledgeManagement /></RequireManager>} />
        <Route path="/manager/oof" element={<RequireManager><OOFManager /></RequireManager>} />
        <Route path="/admin/oof" element={<RequireAdmin><OOFManager /></RequireAdmin>} />
        <Route path="/agent/knowledge" element={<RequireAgent><KnowledgeManagement /></RequireAgent>} />
        <Route path="/admin/project-dashboard" element={<RequireStaff><ProjectsDashboard /></RequireStaff>} />
        <Route path="/admin/projects" element={<RequireStaff><Projects /></RequireStaff>} />
        <Route path="/admin/projects/:id" element={<RequireStaff><ProjectDetail /></RequireStaff>} />
        <Route path="/admin/project-templates" element={<RequireStaff><ProjectTemplates /></RequireStaff>} />
      </Routes>
      </div>
      {!user && <Footer />}
      {showChangePwd && <ChangePwdModal onClose={() => setShowChangePwd(false)} />}
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <MobileNavProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </MobileNavProvider>
    </AuthProvider>
  );
}
